import React, { useMemo } from 'react'

import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import { BLOCKS, INLINES } from '@contentful/rich-text-types'

import { get } from 'helpers/legalPage/get'

import LegalPageAlert from '../LegalPageAlert/LegalPageAlert'
import LegalPageFaq from '../LegalPageFaq/LegalPageFaq'
import LegalPageLead from '../LegalPageLead/LegalPageLead'

const contentElement = {
  [BLOCKS.HEADING_1]: 'h1',
  [BLOCKS.HEADING_2]: 'h2',
  [BLOCKS.HEADING_3]: 'h3',
  [BLOCKS.HEADING_4]: 'h4',
}

export const CONTENT_TYPES = {
  LEGAL_PAGE_ALERT: 'LegalPageAlert',
  LEGAL_PAGE_LEAD: 'LegalPageLead',
  LEGAL_PAGE_FAQ_ENTRY: 'LegalPageFaqEntry',
  LEGAL_PAGE_FAQ_SECTION: 'LegalPageFaqSection',
}

const getNodeId = node => get(node, 'data.target.sys.id')

const getNodeType = node => get(node, '__typename')

const getLegalPageLeadContent = node => get(node, 'legalPageLeadContent')

const getLegalPageAlertContent = node => get(node, 'content.json')

const getLegalPageAlertHeading = node => get(node, 'heading')

const getLegalPageFaqTitle = node => get(node, 'title')

const getLegalPageFaqAnchor = node => get(node, 'linkAnchor')

const getLegalPageFaqEntries = node => get(node, 'entriesCollection.items')

const rxNonAlphanumeric = /[^a-z]+/g

export const getAnchorFromTitle = (title = '') => title.trim().toLowerCase().replace(rxNonAlphanumeric, '-')

const renderHeadingWithNamedAnchors = node => {
  const title = node.content[0].value ?? ''
  const element = contentElement[node.nodeType] ?? 'span'
  const id = getAnchorFromTitle(title)
  const heading = React.createElement(element, {}, title)
  return (
    <>
      <a id={`${id}`} className="named-anchor" href="!#" />
      {heading}
    </>
  )
}

const getDefaultNodes = links => ({
  [BLOCKS.HEADING_1]: renderHeadingWithNamedAnchors,
  [BLOCKS.HEADING_2]: renderHeadingWithNamedAnchors,
  [BLOCKS.HEADING_3]: renderHeadingWithNamedAnchors,
  [BLOCKS.EMBEDDED_ENTRY]: node => {
    const nodeId = getNodeId(node)
    const nodeType = getNodeType(links[nodeId])

    switch (nodeType) {
      case CONTENT_TYPES.LEGAL_PAGE_LEAD:
        return <LegalPageLead>{getLegalPageLeadContent(links[nodeId]) ?? ''}</LegalPageLead>

      case CONTENT_TYPES.LEGAL_PAGE_ALERT:
        return (
          <LegalPageAlert
            heading={getLegalPageAlertHeading(links[nodeId])}
            content={getLegalPageAlertContent(links[nodeId])}
          />
        )

      case CONTENT_TYPES.LEGAL_PAGE_FAQ_SECTION:
        return (
          <LegalPageFaq
            title={getLegalPageFaqTitle(links[nodeId])}
            anchor={getLegalPageFaqAnchor(links[nodeId])}
            entries={getLegalPageFaqEntries(links[nodeId])}
          />
        )

      default:
        return <div className={`unrecognized-content-type type-${nodeType}`} />
    }
  },
  [INLINES.EMBEDDED_ENTRY]: node => {
    const nodeId = getNodeId(node)
    const nodeType = getNodeType(links[nodeId])

    return <div className={`unrecognized-content-type type-${nodeType}`} />
  },
})

const getOptions = ({ links }) => {
  return {
    renderNode: {
      ...getDefaultNodes(links),
    },
  }
}

const ContentfulDocument = ({ content, links }) => {
  const options = useMemo(() => {
    return getOptions({ links })
  }, [links])
  const renderedContent = documentToReactComponents(content, options)

  return <>{renderedContent}</>
}

export default ContentfulDocument
